import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import { getOrderAPI } from "../../../../Services/checkoutService";
import { ArrowLeft, Box, CreditCard, MapPin, CheckCircle2, ChevronRight } from "lucide-react";
import { Button } from "../../../ui/Button";

const OrderDetailsPage = () => {
    const { orderId } = useParams();
    const history = useHistory();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchOrder = async () => {
            setLoading(true);
            const res = await getOrderAPI(orderId);
            if (res.success) {
                setOrder(res.order);
            } else {
                setError(res.message);
            }
            setLoading(false);
        };
        fetchOrder();
    }, [orderId]);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-40">
                <div className="w-10 h-10 border-4 border-red-200 border-t-red-500 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="text-center py-20 bg-white rounded-[32px] border border-neutral-100 shadow-sm max-w-2xl mx-auto mt-10">
                <p className="text-red-500 italic font-medium mb-4">{error || "Order not found."}</p>
                <Button variant="secondary" onClick={() => history.goBack()} className="rounded-full">
                    Go Back
                </Button>
            </div>
        );
    }

    return (
        <div className="bg-grey-500 min-h-screen py-10 px-4">
            <div className="max-w-4xl mx-auto">
                <button
                    onClick={() => history.goBack()}
                    className="group flex items-center gap-3 text-neutral-400 hover:text-red-400 transition-all mb-8"
                >
                    <div className="w-10 h-10 rounded-full border border-neutral-200 flex items-center justify-center group-hover:border-red-400 transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                    </div>
                    <span className="text-sm font-bold uppercase tracking-widest">Back to Orders</span>
                </button>

                <div className="bg-white rounded-[40px] p-8 sm:p-12 mb-8 shadow-sm border border-neutral-100 relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-red-50/50 rounded-full blur-3xl -mr-20 -mt-20 z-0"></div>
                    <div className="relative z-10">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-6 mb-10 border-b border-neutral-100 pb-8">
                            <div>
                                <h1 className="text-3xl font-bold italic text-neutral-900 mb-2">Order Details</h1>
                                <p className="text-neutral-500 font-medium">#{order._id}</p>
                            </div>
                            <div className={`px-4 py-2 ${order.status === 'completed' || order.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'} rounded-full font-bold uppercase tracking-widest text-xs flex items-center gap-2`}>
                                {order.status === 'completed' || order.status === 'paid' ? <CheckCircle2 className="w-4 h-4" /> : <div className="w-2 h-2 rounded-full bg-orange-500 animate-pulse"></div>}
                                {order.status || 'Pending'}
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                            {/* Left Col */}
                            <div className="space-y-10">
                                <div>
                                    <h3 className="text-sm font-black uppercase tracking-widest text-neutral-400 mb-6 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-red-400" />
                                        Shipping details
                                    </h3>
                                    <div className="bg-neutral-50/70 p-6 rounded-3xl border border-neutral-100 space-y-2 text-neutral-700 font-medium">
                                        <p className="font-bold text-neutral-900 mb-2">
                                            {order.customerDetails?.firstName} {order.customerDetails?.lastName}
                                        </p>
                                        <p>{order.customerDetails?.address}</p>
                                        <p>{order.customerDetails?.city}, {order.customerDetails?.postalCode}</p>
                                        <p>{order.customerDetails?.country}</p>
                                        <p className="pt-2 mt-2 border-t border-neutral-200/50">
                                            {order.customerDetails?.email} • {order.customerDetails?.phone}
                                        </p>
                                    </div>
                                </div>

                                <div>
                                    <h3 className="text-sm font-black uppercase tracking-widest text-neutral-400 mb-6 flex items-center gap-2">
                                        <CreditCard className="w-4 h-4 text-red-400" />
                                        Payment Summary
                                    </h3>
                                    <div className="bg-neutral-50/70 p-6 rounded-3xl border border-neutral-100 space-y-4 font-medium text-neutral-500">
                                        <div className="flex justify-between items-center">
                                            <span>Subtotal</span>
                                            <span className="text-neutral-900 font-bold">€{order.subtotal?.toFixed(2)}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span>Shipping</span>
                                            <span className="text-neutral-900 font-bold">{order.shippingCost?.toFixed(2)}</span>
                                        </div>
                                        <div className="pt-4 border-t border-neutral-200/50 flex justify-between items-center text-lg">
                                            <span className="font-black uppercase tracking-widest text-xs">Total paid</span>
                                            <span className="text-red-500 font-bold italic">€{order.totalAmount?.toFixed(2)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Right Col */}
                            <div>
                                <h3 className="text-sm font-black uppercase tracking-widest text-neutral-400 mb-6 flex items-center gap-2">
                                    <Box className="w-4 h-4 text-red-400" />
                                    Items ordered
                                </h3>
                                <div className="space-y-4">
                                    {(order.items || order.cartItems)?.map((item, index) => {
                                        // The product details may be populated under giftId
                                        const product = item.giftId || item;
                                        const title = item.title || item.name || "Unknown Item";
                                        const image = item.img || item.imageUrl || "https://placehold.co/400x400/f5f5f5/a3a3a3?text=Gift";
                                        const price = item.price || item.subtotal || 0;

                                        return (
                                            <div key={index} className="flex gap-4 p-4 rounded-3xl border border-neutral-100 bg-white group hover:shadow-xl hover:shadow-neutral-200/40 transition-all">
                                                <div className="w-20 h-20 bg-neutral-100 rounded-[20px] overflow-hidden flex-shrink-0">
                                                    <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                                </div>
                                                <div className="flex flex-col justify-center flex-1 pr-2">
                                                    <h4 className="font-bold text-neutral-900 leading-tight mb-1">{title}</h4>
                                                    <div className="flex justify-between items-center mt-auto">
                                                        <span className="text-xs font-black uppercase tracking-widest text-neutral-400">Qty: {item.quantity || 1}</span>
                                                        <span className="font-bold italic text-neutral-800">€{(price * (item.quantity || 1))?.toFixed(2)}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrderDetailsPage;
